import {
  ITelegramActiveBot,
  ITelegramBotCrmRequest,
  IToggleTelegramBot,
  ISendTelegramNotification,
  ITelegramBotNotificationHistory,
  ITelegramBotUser,
  TelegramBotConfig,
} from "@lib/types/TelegramChatBot";
import { API } from "@lib/services/http";
import { ApiParams, PaginationType } from "@lib/types/Api";
import { ApiUrlPrepare } from "@lib/helpers/ApiHelper";

export function getActiveTelegramBot() {
  const data: ITelegramBotCrmRequest = {
    method: "GET",
    path: "telegram-bot/active",
  };

  return API.post<ITelegramActiveBot>("telegram-bot-resource", data).then(
    (response) => response.data,
  );
}

export function getTelegramBotConfig(id?: string) {
  const data: ITelegramBotCrmRequest = {
    method: "GET",
    path: `telegram-bot/${id}`,
  };

  return API.post<TelegramBotConfig>(`telegram-bot-resource`, data);
}

export async function getAllTelegramBotConfig<T extends ApiParams>(params: T) {
  const query = ApiUrlPrepare({ params });

  const data: ITelegramBotCrmRequest = {
    method: "GET",
    path: "telegram-bot",
    params: query.replace("?", ""),
  };

  return telegramBotResource(data);
}

export function createOneTelegramBotConfig(value: TelegramBotConfig) {
  const data: ITelegramBotCrmRequest = {
    method: "POST",
    path: "telegram-bot",
    data: value,
  };

  return telegramBotResource(data);
}

export function updateOneTelegramBotConfig({
  id,
  payload,
}: {
  id: string;
  payload: Partial<TelegramBotConfig>;
}) {
  const data: ITelegramBotCrmRequest = {
    method: "PATCH",
    path: `telegram-bot/${id}`,
    data: payload,
  };

  return telegramBotResource(data).then((response) => response.data);
}

export function toggleTelegramBotConfig({
  id,
  payload,
}: {
  id: string;
  payload: IToggleTelegramBot;
}) {
  const data: ITelegramBotCrmRequest = {
    method: "PATCH",
    path: `telegram-bot/toggle/${id}`,
    data: payload,
  };

  return telegramBotResource(data);
}

export function deleteOneTelegramBotConfig(id: string) {
  const data: ITelegramBotCrmRequest = {
    method: "DELETE",
    path: `telegram-bot/${id}`,
  };

  return telegramBotResource(data);
}

export function telegramBotResource(data: ITelegramBotCrmRequest) {
  return API.post<PaginationType<TelegramBotConfig>>(
    `telegram-bot-resource`,
    data,
  );
}

function prepareNotificationQuery(filterString: string) {
  const userJoins = localStorage.getItem("leadsTableJoins");

  if (!userJoins) {
    return filterString;
  }

  const queryParams = new Set();

  const join = "&join=";
  const joinsArr = userJoins.split(join);
  const filtersArr = filterString.split("filter=");

  filtersArr.forEach((value) => {
    if (value) {
      const key = value.split("||")[0].split(".")[0];
      if (joinsArr.some((v) => v.startsWith(key))) {
        queryParams.add(join.concat(key));
      }
    }
  });

  return filterString + Array.from(queryParams).join("");
}

export function getEligibleUserCount(filters: string) {
  return API.get<number>(
    `telegram-bot-notification-queue/count?${prepareNotificationQuery(filters)}`,
  ).then((response) => response.data);
}

export function sendTelegramNotification(payload: {
  data: ISendTelegramNotification;
  filters: string;
}) {
  return API.post(
    `telegram-bot-notification-queue?${prepareNotificationQuery(payload.filters)}`,
    payload.data,
  ).then((response) => response.data);
}

export function getTelegramNotificationHistory<T extends ApiParams>(params: T) {
  const query = ApiUrlPrepare({ params });

  return API.get<PaginationType<ITelegramBotNotificationHistory>>(
    `telegram-bot-notification-history?${query.replace("?", "")}`,
  ).then((response) => response.data);
}

export function getTelegramBotUsers<T extends ApiParams>(params: T) {
  const url = ApiUrlPrepare({ params, url: "manager-telegram-bot-config" });

  return API.get<PaginationType<ITelegramBotUser>>(url);
}

export function updateUserTelegramChatId({
  telegramPayload,
  interactedBots,
}: {
  telegramPayload: string;
  interactedBots: string[];
}) {
  return API.post("/manager-telegram-bot-config/save-manager-config", {
    telegramPayload,
    interactedBots,
  });
}
